import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ConnectedPosition } from '@angular/cdk/overlay';
import { catchError, debounceTime, map, of, Subject, switchMap } from 'rxjs';
import { WarehouseService } from '../../../services/warehouse.service';
import { StockTotals, VariantSummary } from '../warehouse.types';
import { formatQuantity, getVariantCodes } from '../warehouse.utils';
import { formatMoneyAmount } from '../../board-v3/price-field.utils';

/** A found variant as card items and document lines show it, with its stock over all warehouses */
export type VariantPick = VariantSummary & { stock: StockTotals };

/**
 * Search field for variants of the catalog: by name, SKU or barcode. Enter picks the first result,
 * so a barcode scanner adds the scanned product right away.
 */
@Component({
  selector: 'app-warehouse-variant-picker',
  templateUrl: './variant-picker.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
  host: { class: 'block min-w-0' },
})
export class WarehouseVariantPickerComponent implements OnInit {
  @Input() placeholder = '';
  @Input() disabled = false;
  /** Lines or items picked already, marked in the results */
  @Input() selected: { variant_id: string }[] = [];
  @Output() picked = new EventEmitter<VariantPick>();
  @ViewChild('origin') private origin?: ElementRef<HTMLElement>;
  @ViewChild('searchInput') private searchInput?: ElementRef<HTMLInputElement>;

  private readonly warehouseService = inject(WarehouseService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly search$ = new Subject<string>();

  protected readonly positions: ConnectedPosition[] = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4 },
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -4 },
  ];
  protected query = '';
  protected results: VariantPick[] = [];
  protected searching = false;
  protected open = false;
  protected overlayWidth = 0;
  protected activeIndex = 0;
  /** Enter was pressed while the search was running: the first result is picked when it arrives */
  private pickFirstOnResults = false;

  protected readonly formatQuantity = formatQuantity;
  protected readonly formatMoneyAmount = formatMoneyAmount;
  protected readonly getVariantCodes = getVariantCodes;

  ngOnInit(): void {
    this.search$
      .pipe(
        debounceTime(250),
        switchMap((search) =>
          this.warehouseService.getProducts({ search, take: 20, is_archived: false }).pipe(
            // The search finds products, so every variant of a matched product comes back
            map(({ items }) =>
              items.flatMap((product) =>
                product.variants
                  .filter((variant) => !variant.is_archived)
                  .map((variant) => ({
                    ...variant,
                    product_id: product.id,
                    product_name: product.name,
                    photo_id: variant.photo_id ?? product.photo_ids[0] ?? null,
                    unit: product.unit?.short_name ?? null,
                    currency: product.currency,
                  })),
              ),
            ),
            // A typed or scanned SKU / barcode names one variant: it goes first, so Enter picks exactly it
            map((picks) => {
              const code = search.toLowerCase();
              const isExact = (pick: VariantPick): boolean =>
                pick.sku?.toLowerCase() === code || pick.barcode?.toLowerCase() === code;
              return [...picks.filter(isExact), ...picks.filter((pick) => !isExact(pick))];
            }),
            catchError(() => of([])),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((results) => {
        this.results = results;
        this.activeIndex = 0;
        this.searching = false;
        if (this.pickFirstOnResults && results.length) this.pick(results[0]);
        this.pickFirstOnResults = false;
      });
  }

  protected isSelected(pick: VariantPick): boolean {
    return this.selected.some((item) => item.variant_id === pick.id);
  }

  protected onQueryChange(value: string): void {
    this.query = value;
    this.pickFirstOnResults = false;
    this.searching = this.open = !!value.trim();
    if (this.open) {
      this.overlayWidth = Math.max(this.origin?.nativeElement.getBoundingClientRect().width ?? 0, 360);
      this.search$.next(value.trim());
    } else {
      this.results = [];
    }
  }

  protected onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') this.open = false;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const step = event.key === 'ArrowDown' ? 1 : -1;
      if (this.results.length) this.activeIndex = (this.activeIndex + step + this.results.length) % this.results.length;
    }
    if (event.key !== 'Enter' || !this.query.trim()) return;

    event.preventDefault();
    if (this.searching) this.pickFirstOnResults = true;
    else if (this.results.length) this.pick(this.results[this.activeIndex] ?? this.results[0]);
  }

  protected onOutsideClick(event: MouseEvent): void {
    if (!this.origin?.nativeElement.contains(event.target as Node | null)) this.open = false;
  }

  protected pick(result: VariantPick): void {
    this.picked.emit(result);
    this.query = '';
    this.results = [];
    this.open = false;
    setTimeout(() => this.searchInput?.nativeElement.focus());
  }
}
